/**
 * 连线把手外挂热区几何：90×90、按钮默认距边 12px、指针中心对齐 + clamp。
 * 热区本身无视觉（无底无描边），仅作命中与跟随范围。
 * 按钮显隐：节点 hover（CSS group-hover）即显示默认位；进入热区后跟随；本侧起拖强制隐藏。
 */

export type CanvasConnectHotzoneRole = 'source' | 'target';

export const CANVAS_CONNECT_HOTZONE_SIZE = 90;
export const CANVAS_CONNECT_BUTTON_SIZE = 32;
export const CANVAS_CONNECT_DEFAULT_EDGE_GAP = 12;

export interface ConnectButtonOffset {
  left: number;
  top: number;
}

export type ConnectButtonVisibilityMode = 'force-hidden' | 'force-visible' | 'group-hover';

/** 默认：距卡片边 12px、热区内垂直居中。source 靠热区左（贴卡边外），target 靠热区右。 */
export function getDefaultConnectButtonOffset(
  role: CanvasConnectHotzoneRole,
  options?: {
    hotzoneSize?: number;
    buttonSize?: number;
    edgeGap?: number;
  }
): ConnectButtonOffset {
  const hotzoneSize = options?.hotzoneSize ?? CANVAS_CONNECT_HOTZONE_SIZE;
  const buttonSize = options?.buttonSize ?? CANVAS_CONNECT_BUTTON_SIZE;
  const edgeGap = options?.edgeGap ?? CANVAS_CONNECT_DEFAULT_EDGE_GAP;
  const top = (hotzoneSize - buttonSize) / 2;

  if (role === 'source') {
    return { left: edgeGap, top };
  }

  return { left: hotzoneSize - edgeGap - buttonSize, top };
}

/**
 * 按钮几何中心对齐指针，再把按钮矩形 clamp 在热区内。
 * pointer 为相对热区左上角的 local 坐标。
 */
export function clampConnectButtonToPointer(
  pointerX: number,
  pointerY: number,
  options?: {
    hotzoneSize?: number;
    buttonSize?: number;
  }
): ConnectButtonOffset {
  const hotzoneSize = options?.hotzoneSize ?? CANVAS_CONNECT_HOTZONE_SIZE;
  const buttonSize = options?.buttonSize ?? CANVAS_CONNECT_BUTTON_SIZE;
  const half = buttonSize / 2;
  const max = hotzoneSize - buttonSize;

  return {
    left: Math.min(max, Math.max(0, pointerX - half)),
    top: Math.min(max, Math.max(0, pointerY - half)),
  };
}

/**
 * 按钮显隐模式：
 * - force-hidden：本侧已起拖，立刻隐藏（覆盖 group-hover）
 * - force-visible：对侧合法目标 reveal
 * - group-hover：悬停节点即显，默认距边 12px；进热区后由指针跟随
 */
export function resolveConnectButtonVisibilityMode(params: {
  isConnectionFromThisHandle: boolean;
  revealForConnection: boolean;
}): ConnectButtonVisibilityMode {
  if (params.isConnectionFromThisHandle) return 'force-hidden';
  if (params.revealForConnection) return 'force-visible';
  return 'group-hover';
}
